import { useI18n } from "vue-i18n";
import { useToast } from "@eudonet/opal";
import { saveFilter, saveFilterChanges, removeFilter, getFilterDetail, } from "@/shared/infrastructure/adapters/filterAdapter";
import { invalidateFilters, } from "@/shared/composables/useFilters";
import { SAVED_FILTER_UNAVAILABLE_KEYS } from "@/shared/infrastructure/savedFilterErrorCodes";
/**
 * errorCode -> i18n key. The codes are the wire contract emitted by the OPREF
 * saved-filter validators and the duplicate-name guard; renaming one on either
 * side silently breaks the mapping, so both are covered by tests.
 *
 * OPREF's ValidationException surfaces only the FIRST explicit code among the failures, so a
 * request breaking several rules maps to one of them; the client-side length check in both
 * modals still catches "too long" before the request is sent.
 */
export const SAVED_FILTER_ERROR_KEYS = {
    "SavedFilter.Name.Required": "advancedFilterLibrary.save.errors.nameRequired",
    "SavedFilter.Name.TooLong": "advancedFilterLibrary.save.errors.nameTooLong",
    "SavedFilter.Name.Duplicate": "advancedFilterLibrary.save.errors.nameDuplicate",
    ...SAVED_FILTER_UNAVAILABLE_KEYS,
};
export function useSavedFilterMutations(entity, application) {
    const { t } = useI18n();
    const { showToast } = useToast();
    function toastSuccess(key, named) {
        showToast({
            message: t(key, named ?? {}),
            ariaClose: t("form.dismissToast"),
            status: "success",
            duration: 5000,
        });
    }
    // Every mutation rethrows: the caller (a modal) renders the failure inline
    // under the name field via resolveApiErrorMessage + SAVED_FILTER_ERROR_KEYS.
    // Toast + cache invalidation only happen on the success path.
    async function create(input, options = {}) {
        const created = await saveFilter({
            application,
            name: input.name,
            entityType: entity,
            // Always Personal: opref accepts access grants on Personal filters only
            // (`SavedFilter.Visibility.NotShareable`), so sharing is applied afterwards
            // through grants (`useSavedFilterLifecycle`). Workspace stays the
            // admin-curated tier and must not be created from here.
            visibility: "Personal",
            criteria: input.criteria,
        });
        if (options.announce ?? true) {
            toastSuccess("advancedFilterLibrary.toast.created", {
                name: created.name,
            });
        }
        invalidateFilters(entity);
        return created;
    }
    /**
     * Saves a new criteria document over an existing filter, keeping its name.
     *
     * The name is supplied by the caller rather than re-fetched: OPREF's PUT requires BOTH
     * fields, and `useSavedFilterLifecycle` already holds the name from the GET that opened the
     * builder in edit mode. Re-fetching it here would cost a second BFF round-trip (plus an
     * OPREF hop on a cold detail cache) on every edit-save, and add a failure point that makes
     * the save fail when nothing was wrong with the write.
     */
    async function update(id, name, criteria) {
        const saved = await saveFilterChanges(id, { name, criteria });
        toastSuccess("advancedFilterLibrary.toast.updated", { name: saved.name });
        invalidateFilters(entity);
        return saved;
    }
    async function rename(id, name) {
        // OPREF's PUT validates Criteria as non-empty, so a name-only change still
        // has to re-send the stored document.
        const current = await getFilterDetail(id);
        const saved = await saveFilterChanges(id, {
            name,
            criteria: current.criteria,
        });
        toastSuccess("advancedFilterLibrary.toast.renamed", { name: saved.name });
        invalidateFilters(entity);
        return saved;
    }
    async function remove(id, name) {
        await removeFilter(id);
        toastSuccess("advancedFilterLibrary.toast.deleted", { name });
        invalidateFilters(entity);
    }
    return { create, update, rename, remove, toastSuccess };
}
